import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../layouts/Modal';
import UserDataForm from '../forms/userData/UserDataForm';
import { ProfileField } from '../../types/profile';
import { connectionService } from '../../services';
import Button from '../misc/Button';

interface ShareWithConnectionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  connectionId: number;
  connectionName: string;
}

export default function ShareWithConnectionModal({
  open,
  setOpen,
  connectionId,
  connectionName,
}: ShareWithConnectionModalProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    connectionService
      .getSharedDataWithUser(connectionId.toString())
      .then((fields) => setSelectedData(fields))
      .catch(() => setSelectedData([]))
      .finally(() => setLoading(false));
  }, [open, connectionId]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const ids = selectedData.map((f) => f.id!.toString());
      await connectionService.updateConnection(connectionId.toString(), ids);
      toast.success(t('toast.success.updateSharedData'));
      setOpen(false);
    } catch (e) {
      toast.error(t('toast.error.general'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={t('page.home.connections.share')}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            {t('general.cancel')}
          </Button>
          <Button type="button" variant="primary" onClick={handleSave} disabled={loading}>
            {t('general.save')}
          </Button>
        </div>
      }
      maxWidth="max-w-2xl">
      <div className="min-h-[200px]">
        <p className="mb-4 text-sm">
          {t('page.home.connections.shareDescription', { name: connectionName.replace('+', ' ') })}
        </p>
        <UserDataForm selectedData={selectedData} setSelectedData={setSelectedData} hasSelectAll />
      </div>
    </Modal>
  );
}
